import { SubmitHandler, useForm } from 'react-hook-form';
import {
  Button,
  HStack,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Text,
  Textarea,
  VStack,
} from 'ui_2';
import { supportedCountryCodes, zodCreateServiceInput } from 'jigsaw';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpcReact } from '@src/data/trpc';
import z from 'zod';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { ImagePreview } from './ImagePreview';
import { countries, getEmojiFlag, TCountryCode } from 'countries-list';
import { useBeforeUnloadWarning } from '@src/hooks/useBeforeUnloadWarning';

type FormInput = z.infer<typeof zodCreateServiceInput>;

export const CreateService = () => {
  const { data: sponsors } = trpcReact.getSponsors.useQuery({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isLoading, isSubmitting, isDirty, isValid },
  } = useForm({
    resolver: zodResolver(zodCreateServiceInput),
  });

  useBeforeUnloadWarning(isDirty);

  const watchedValues = watch();

  const { error, mutateAsync } = trpcReact.createStoreService.useMutation({
    onSuccess: () => {
      toast.success(`Created new Service: ${watchedValues.title}!`);
    },
  });

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    await mutateAsync(data);
  };

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  return (
    <VStack className='mx-auto w-full space-y-6'>
      <Text font='sm' color='gray-600'>
        ⚠️ Note: Optional fields not defined here must be defined in the Product
        SKU ⚠️
      </Text>
      <form onSubmit={handleSubmit(onSubmit)} className='w-full space-y-4'>
        {/* Basic Info */}
        <div className='flex flex-col items-center gap-4 md:flex-row'>
          <Input
            {...register('title', {
              onChange: (e) => {
                const title = e.target.value;
                const slug = title
                  .toLowerCase()
                  .replace(/\s+/g, '')
                  .replace(/[^a-z0-9-]/g, '');
                setValue('slug', slug);
              },
            })}
            label='Service Title'
            placeholder='Uber'
            error={errors.title?.message?.toString()}
          />
          <Input
            {...register('url')}
            label='Service URL'
            placeholder='https://uber.com'
            type='url'
            error={errors.url?.message?.toString()}
          />
        </div>

        <HStack className='items-center gap-4'>
          <Input
            {...register('codeLength', {
              valueAsNumber: true,
            })}
            label='Code Length (Optional)'
            placeholder='16'
            type='number'
            step={1}
            error={errors.codeLength?.message?.toString()}
          />

          <Input
            {...register('priceRange')}
            label='Price Range'
            placeholder='100 - 10,000 PART'
            error={errors.priceRange?.message?.toString()}
          />
        </HStack>

        <HStack className='gap-4'>
          <VStack>
            <Select
              onValueChange={(countryCode: TCountryCode) => {
                setValue('countries', [countryCode]);
                setValue('availability', countries[countryCode].name);
              }}
            >
              <SelectTrigger className='w-[180px]'>
                <SelectValue placeholder='Country (Optional)' />
              </SelectTrigger>
              <SelectContent className='max-h-[280px]'>
                {supportedCountryCodes.map((countryCode, ix) => (
                  <SelectItem value={countryCode} key={ix}>
                    <Text>
                      {getEmojiFlag(countryCode)} {countries[countryCode].name}
                    </Text>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.countries?.message && (
              <Text color={'red'} font={'regular-xs'}>
                {errors.countries.message}
              </Text>
            )}
            {errors.availability?.message && (
              <Text color={'red'} font={'regular-xs'}>
                {errors.availability.message}
              </Text>
            )}
          </VStack>

          <VStack>
            <Select
              onValueChange={(sponsorId: string) => {
                setValue('sponsorId', sponsorId);
              }}
            >
              <SelectTrigger className='w-[180px]'>
                <SelectValue placeholder='Sponsor' />
              </SelectTrigger>
              <SelectContent>
                {sponsors?.map((sponsor, ix) => (
                  <SelectItem value={sponsor._id} key={ix}>
                    <Text>{sponsor.name}</Text>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.sponsorId?.message && (
              <Text color={'red'} font={'regular-xs'}>
                {errors.sponsorId.message}
              </Text>
            )}
          </VStack>
        </HStack>

        <Input
          {...register('redeemURL')}
          label='Redemption URL (Optional)'
          placeholder='https://uber.com'
          type='url'
          error={errors.redeemURL?.message?.toString()}
        />

        <Textarea
          {...register('redemptionInstructions')}
          label='Redemption Instructions (optional)'
          placeholder='Go to uber.com to redeem.'
          rows={3}
          error={errors.redemptionInstructions?.message?.toString()}
          className='w-full'
        />

        <Textarea
          {...register('termsAndConditions')}
          label='Service Terms and Conditions (optional)'
          placeholder='For full terms and conditions...'
          rows={3}
          error={errors.termsAndConditions?.message?.toString()}
          className='w-full'
        />

        {/* Logos */}
        <div className='flex flex-col items-center gap-4 md:flex-row'>
          <Input
            {...register('bigLogoImageURL')}
            type='url'
            label='Big Logo URL'
            placeholder='https://img.com/1234/big'
            error={errors.bigLogoImageURL?.message?.toString()}
            rightElement={
              watchedValues.bigLogoImageURL ? (
                <ImagePreview
                  url={watchedValues.bigLogoImageURL}
                  label='Big Logo'
                />
              ) : undefined
            }
          />

          <Input
            {...register('smallLogoImageURL')}
            type='url'
            label='Small Logo URL'
            placeholder='https://img.com/1234/small'
            error={errors.smallLogoImageURL?.message?.toString()}
            rightElement={
              watchedValues.smallLogoImageURL ? (
                <ImagePreview
                  url={watchedValues.smallLogoImageURL}
                  label='Small Logo'
                />
              ) : undefined
            }
          />
        </div>

        {/* Password at bottom */}
        <Input
          {...register('password')}
          type='password'
          label='Password'
          placeholder='Enter password'
          error={errors.password?.message?.toString()}
        />

        <Button
          type='submit'
          disabled={isLoading || isSubmitting || !isValid}
          className='w-full'
          variant='primary'
        >
          {isSubmitting ? (
            <HStack className='items-center gap-2'>
              <Text>Creating...</Text>
              <Spinner />
            </HStack>
          ) : (
            'Create Sponsor'
          )}
        </Button>

        {errors.root?.message && (
          <Text color='red' className='text-center' font='xs'>
            {errors.root.message}
          </Text>
        )}
      </form>
    </VStack>
  );
};
